import * as i0 from '@angular/core';
import { Component, ViewEncapsulation, Optional, Self, Input, HostBinding, HostListener, NgModule } from '@angular/core';
import { coerceNumberProperty, coerceBooleanProperty } from '@angular/cdk/coercion';
import Cleave from 'cleave.js';
import { InputDirective } from '@eui/components/shared';
import { getLocaleNumberSymbol, NumberSymbol, CommonModule } from '@angular/common';
import * as i1 from '@angular/forms';
import * as i2 from '@eui/core';

class EuiInputNumberComponent extends InputDirective {
    constructor(control, _elementRef, _renderer, cd, localeService, injector) {
        super(_elementRef, _renderer, control, injector);
        this.control = control;
        this._elementRef = _elementRef;
        this._renderer = _renderer;
        this.cd = cd;
        this.localeService = localeService;
        this._leadingZero = 0;
        this.type = 'eui-number';
        this._fractionDigits = 0;
        this.onChange = (_) => {
            if (this.control && (_ !== null || _ !== undefined)) {
                // parse number
                const value = Number(_);
                // run on next MicroTask to avoid NG0100 error TODO: deeper investigation
                Promise.resolve().then(() => this.control.viewToModelUpdate(value));
                // in case control is FormControl
                this.control?.control.setValue(isNaN(value) ? null : value, { emitEvent: false, emitModelToViewChange: false });
            }
        };
        this.onTouched = () => {
            this.isInvalid = this.control.invalid;
        };
        // set default options
        this.options = {
            numeral: true,
        };
    }
    /**
     * The minimum number can be entered. Blocks user's input if not in range.
     */
    get min() {
        return this._min;
    }
    set min(value) {
        this._min = coerceNumberProperty(value);
    }
    /**
     * The maximum number can be entered. Blocks user's input if not in range.
     */
    get max() {
        return this._max;
    }
    set max(value) {
        this._max = coerceNumberProperty(value);
    }
    /** Adds leading zero to a number. Formatting will not work.
     *      e.g. with leadingZero=3 input number 5 => 005
     */
    get leadingZero() {
        return this._leadingZero;
    }
    set leadingZero(value) {
        this._leadingZero = coerceNumberProperty(value);
    }
    get isInvalid() {
        return this._isInvalid || null;
    }
    set isInvalid(state) {
        this.setInvalid(state);
    }
    get cssClasses() {
        return [
            super.getCssClasses('eui-input-number'),
            this._isInvalid ? `eui-input-number--invalid` : '',
        ].join(' ').trim();
    }
    /** how many digits for the decimal part */
    get fractionDigits() { return this._fractionDigits; }
    set fractionDigits(value) { this._fractionDigits = coerceNumberProperty(value); }
    /** how many digits for the integer part */
    get digits() { return this._digits; }
    set digits(value) { this._digits = coerceNumberProperty(value); }
    get fillFraction() { return this._fillFraction; }
    set fillFraction(value) { this._fillFraction = coerceBooleanProperty(value); }
    /** rounds a number with more than two decimals UP */
    get roundUp() { return this._roundUp; }
    set roundUp(value) { this._roundUp = coerceNumberProperty(value); }
    /**
     * does not format the and do a thousand grouping of number
     */
    get noFormat() { return this._noFormat; }
    set noFormat(value) { this._noFormat = coerceBooleanProperty(value); }
    ngOnInit() {
        super.ngOnInit();
        // set controlValueAccessor
        if (this.control) {
            this.control.valueAccessor = this;
        }
        // instantiate cleave
        this.initCleave();
        // subscribe to localization service and listen for locale change. De-construction of locale should happen here!
        this.localeService.getState().subscribe((state) => {
            this.options = this.getCleaveOptsBasedOnLocale(state?.id);
            // update the options on the cleave instance
            this.updateOptions(true);
        });
    }
    ngDoCheck() {
        if (this.control) {
            this.isInvalid = this.control.invalid && this.control.touched;
        }
    }
    ngOnChanges(changes) {
        super.ngOnChanges(changes);
        if (changes.hasOwnProperty('value')) {
            this._elementRef.nativeElement.value = this.value;
            if (this.cleaveInstance) {
                this.cleaveInstance.setRawValue(this.value);
            }
        }
        if (changes.hasOwnProperty('fractionDigits')) {
            this.options = { ...this.options, numeralDecimalScale: this.fractionDigits };
            this.updateOptions();
        }
        if (changes.hasOwnProperty('noFormat')) {
            this.options = { ...this.options, numeralThousandsGroupStyle: this.noFormat ? 'none' : 'thousand' };
            this.updateOptions();
        }
        if (changes.hasOwnProperty('fillFraction') && this.cleaveInstance) {
            this.applyFractionFill(this.cleaveInstance.getRawValue());
        }
        if (changes.hasOwnProperty('digits')) {
            this.options = { ...this.options, numeralIntegerScale: this.digits };
            this.updateOptions();
        }
        if (changes.hasOwnProperty('min')) {
            if (changes['min'].currentValue >= 0) {
                if (this.cleaveInstance) {
                    // take care of the sign symbol
                    this.cleaveInstance.setRawValue(Math.abs(Number(this.cleaveInstance.getRawValue())).toString());
                }
            }
        }
        if (changes.hasOwnProperty('roundUp')) {
            if (changes['roundUp'].currentValue > 0) {
                if (this.cleaveInstance) {
                    let number = Number.parseFloat(this.cleaveInstance.getRawValue());
                    number = this.decimalAdjust(number, this.roundUp * -1);
                    this.cleaveInstance.setRawValue(number.toString());
                }
            }
        }
    }
    ngOnDestroy() {
        super.ngOnDestroy();
        // destroy cleave instance
        if (this.cleaveInstance) {
            this.cleaveInstance.destroy();
        }
    }
    /**
     * sets the number for cleave instance and div element (read only mode)
     * @param val the number formatted or not
     */
    writeValue(val) {
        if (this.cleaveInstance) {
            if (this.fillFraction && val !== '' && val) {
                this.applyFractionFill(val);
                this.cleaveInstance.setRawValue(val);
            }
            else {
                this.cleaveInstance.setRawValue(val);
            }
            // retrieve formatted value (based on options) and set the number to the model
            const number = Number.parseFloat(this.cleaveInstance.getRawValue());
            this.control.viewToModelUpdate(number);
            this.control?.control.setValue(isNaN(number) ? null : number, { emitEvent: false, emitModelToViewChange: false });
        }
    }
    registerOnChange(fn) {
        this.onChange = fn;
    }
    registerOnTouched(fn) {
        this.onTouched = fn;
    }
    setDisabledState(isDisabled) {
        this.disabled = isDisabled;
    }
    onPaste(event) {
        // get the value from clipboard
        let value = (event.clipboardData || window['clipboardData']).getData('text');
        // in case round UP is enabled
        if (this._roundUp > 0) {
            const div = document.createElement('input');
            // create a clone of cleaveInstance
            const cloneInstance = new Cleave(div, { ...this.options, numeralDecimalScale: 8 });
            cloneInstance.setRawValue(value.toString());
            let number = Number.parseFloat(cloneInstance.getRawValue());
            number = this.decimalAdjust(number, this.roundUp * -1);
            if (!Number.isNaN(number)) {
                this.cleaveInstance.setRawValue(number.toString());
                // TODO: investigate in the future how can this be avoided
                setTimeout(() => {
                    this.writeValue(number.toString());
                }, 0);
            }
        }
    }
    onKeyDown(e) {
        // check if it's a single symbol and not Special like (Ctrl, backspace etc)
        if (e?.key.length <= 1 && !e.ctrlKey && !e.altKey && !e.metaKey) {
            // stop event propagation if key pressed is not a number or locale character or minus sign
            if (Number.isNaN(Number.parseInt(e.key, 10)) && this.isMinusAllowed(e.key) &&
                (e.key !== getLocaleNumberSymbol(this.localeService.currentLocale, NumberSymbol.Decimal))) {
                e.stopPropagation();
                e.preventDefault();
                // stop event propagation if key pressed exceeds the min/max value range
            }
            else if (!Number.isNaN(Number.parseInt(e.key, 10)) && this.isOutSideRange(e)) {
                e.stopPropagation();
                e.preventDefault();
            }
            else if (this.isOutSideRange(e)) {
                e.stopPropagation();
                e.preventDefault();
            }
        }
        // protect decimal symbol deletion in case fillFraction is on
        if ((e.key === 'Delete' || e.key === 'Backspace')) {
            const element = this._elementRef.nativeElement;
            // position of caret
            const pos = element.selectionStart;
            // get character about to be deleted
            const char = element.value.charAt(pos - 1);
            // in case the char is the decimal symbol prevent deletion and move caret in front
            if (char === getLocaleNumberSymbol(this.localeService.currentLocale, NumberSymbol.Decimal)) {
                e.stopPropagation();
                e.preventDefault();
                element.setSelectionRange(pos - 1, pos - 1);
            }
            else if (this.isOutSideRange(e)) { // don't allow insertion that will put number out of range
                e.stopPropagation();
                e.preventDefault();
            }
        }
    }
    onFocusout(e) {
        // in case fillFraction feature is on and there's no integer part add leading 0
        if (this.fillFraction && this.cleaveInstance.getRawValue().match(/^\.[0-9]+/g)) {
            this.cleaveInstance.setRawValue('0' + this.cleaveInstance.getRawValue());
        }
        // in case round UP is enabled
        if (this._roundUp > 0) {
            const number = Number.parseFloat(this.cleaveInstance.getRawValue());
            this.cleaveInstance.setRawValue(this.decimalAdjust(number, this.roundUp * -1).toString());
        }
    }
    setInvalid(state) {
        // in case it's controlled by NgControl override
        this._isInvalid = this.control ? this.control.invalid && this.control.touched : coerceBooleanProperty(state);
        // set BaseDirective Attribute
        super.euiDanger = this._isInvalid;
    }
    /**
     * @Override setPlaceholder setPlaceholderAttribute of InputDirective
     * Updates the placeholder value based on current number format and given value.
     * If value passed contains characters it doesn't format placeholder.
     * */
    setPlaceholderAttribute(value = 999999.9999) {
        // if value is a number, format the value based on current number format
        if (typeof value === 'number' && !isNaN(value)) {
            const div = document.createElement('input');
            // create a clone of cleaveInstance
            const cloneInstance = new Cleave(div, this.options);
            // BEWARE: by the time your setRawValue cleave options have changed thus number formatting
            cloneInstance.setRawValue(value.toString());
            // set the value with current format as placeholder
            super.setPlaceholderAttribute(cloneInstance.getFormattedValue());
        }
        else if (typeof value === 'string') {
            super.setPlaceholderAttribute(value.toString());
        }
        else if (isNaN(value)) {
            value = this._elementRef.nativeElement.placeholder;
            super.setPlaceholderAttribute(value.toString());
        }
    }
    /**
     * instantiate Cleave.js
     */
    initCleave() {
        // change the cleave prototype function to intercept onCut callback
        // and allow based on the event bubbling and disabled state
        // TIP: change behavior of onCut to stop in case bubble is canceled
        // TODO: See relevant issue https://github.com/nosir/cleave.js/issues/675
        const _onCut = Cleave.prototype['onCut'];
        Cleave.prototype['onCut'] = function (e) {
            // eslint-disable-next-line no-invalid-this
            if (!e.cancelBubble && !this.element.disabled) {
                // eslint-disable-next-line no-invalid-this
                _onCut.call(this, e);
            }
        };
        // in case options is undefined
        const opts = { ...this.options };
        // set the onValue change listener to keep the value at readOnly element in sync
        opts.onValueChanged = this.onValueChanged.bind(this);
        // in case round UP is enabled
        if (this._roundUp > 0) {
            let number = Number.parseFloat(this._elementRef.nativeElement.value);
            this._elementRef.nativeElement.value = this.decimalAdjust(number, this.roundUp * -1);
        }
        // create the Cleave instance with the new or updated options
        this.cleaveInstance = new Cleave(this._elementRef.nativeElement, opts);
    }
    /**
     * Updates the cleave options by destroying the current instance and creating another one while keeping the previous
     * value.
     */
    updateOptions(localeUpdated = false) {
        if (this.cleaveInstance) {
            // the value before destroying cleave instance
            const previousValue = this.cleaveInstance.getRawValue();
            // destroy previous instance from the element
            this.cleaveInstance.destroy();
            // while creating the Cleave instance we are passing onValueChanged
            // and bind to 'this' reference. Inside onValueChanged there is a call
            // to this.cleaveInstance which was using the old one, causing loop (max call stack)
            // So it is essential to set it to null before creating a new instance.
            this.cleaveInstance = null;
            // create the Cleave instance with the new or updated options
            this.cleaveInstance = new Cleave(this._elementRef.nativeElement, {
                ...this.options,
                initValue: previousValue,
                onValueChanged: this.onValueChanged.bind(this),
            });
            // In case previous settings where numeralDecimalMark='.' with value 1000.99 and they changed to
            // numeralDecimalMark=','; set initValue to 1000.99 or even 1000,99 will not be reflected and cleave will
            // remove the decimal part. Set of the value after initialization causes reformat of number and works.
            this.cleaveInstance.setRawValue(previousValue);
            // parse number
            const number = this.parseNumber(this._elementRef.nativeElement.placeholder || undefined, localeUpdated);
            // set placeholder in case none is provided
            this.setPlaceholderAttribute(number);
        }
    }
    /**
     * Callback function of the Cleave instance that is invoked when value is changed.
     * @param event type of CleaveEventOnChange
     */
    onValueChanged(event) {
        const { target } = event;
        if (this.fillFraction) {
            this.applyFractionFill(target.rawValue);
        }
        this.onChange(Number.parseFloat(target.rawValue));
        // fill leading zero
        if (this.leadingZero) {
            const size = this.leadingZero > this.digits && this.digits > 0 ? this.digits : this.leadingZero;
            this._elementRef.nativeElement.value = this.padZero(event.target.rawValue, size);
        }
    }
    /**
     * responsible to extract all locale information needed to build CleaveOptions related to number format.
     * @param id The id of the locale
     */
    getCleaveOptsBasedOnLocale(id) {
        if (!id || id === '') {
            throw new Error('Locale id cannot be empty or undefined');
        }
        const opts = {
            numeral: true,
            delimiter: getLocaleNumberSymbol(id, NumberSymbol.Group),
            numeralDecimalMark: getLocaleNumberSymbol(id, NumberSymbol.Decimal),
            numeralDecimalScale: this.fractionDigits,
            numeralIntegerScale: this.digits,
            numeralThousandsGroupStyle: this.noFormat ? 'none' : 'thousand', // TODO: implement based on locale
        };
        return { ...this.options, ...opts };
    }
    /**
     * Applies the fill of decimal part with zeros in case decimal part of given number has
     * size less than the max fraction digits.
     * @param number
     * @private
     */
    applyFractionFill(number) {
        // if fill fraction is enabled and there's cleave instance
        if (this.fillFraction) {
            if (this.cleaveInstance) {
                number = number?.toString();
                if (number === '' || !number) {
                    return;
                }
                const hasDecimal = number.split('.').length > 1;
                // in case there's already decimal point in number
                if (hasDecimal) {
                    const decimalSize = number.split('.')[1].length;
                    // if size of decimal part of number is less than the max fraction digits
                    if (decimalSize < this._fractionDigits) {
                        number = number + new Array(this._fractionDigits - decimalSize).fill(0).join('');
                        const pos = this._elementRef.nativeElement.selectionStart;
                        this.cleaveInstance.setRawValue(number);
                        this._elementRef.nativeElement.setSelectionRange(pos, pos);
                    }
                    // in case there's not, force it by filling it as much zero as the fraction digits
                }
                else if (this.fractionDigits > 0) {
                    number = `${number}.${new Array(this._fractionDigits).fill(0).join('')}`;
                    const pos = this._elementRef.nativeElement.selectionStart;
                    this.cleaveInstance.setRawValue(number);
                    this._elementRef.nativeElement.setSelectionRange(pos, pos);
                }
            }
        }
    }
    /**
     * Parses a given formatted number and returns it as a number.
     * @param value The formatted number you want to parse e.g. 111,888.22
     * @param currentLocaleState Use previous locale state to parse the value. By default, is false
     * @private
     */
    parseNumber(value, userPrevLocaleState = false) {
        // locale state
        const locale = userPrevLocaleState ? this.localeService.previousLocale : this.localeService.currentLocale;
        // get decimal and group symbols
        let decimalSymbol = getLocaleNumberSymbol(locale, NumberSymbol.Decimal);
        let groupSymbol = getLocaleNumberSymbol(locale, NumberSymbol.Group);
        // replace symbols to parse number
        let parsedNumber = value?.split(groupSymbol).join('').split(decimalSymbol).join('.');
        return Number.parseFloat(parsedNumber);
    }
    /**
     * Pad leading zero to the number
     * @param num The number to add leading zero to
     * @param size Number of number's leading zero
     * @private
     */
    padZero(num, size) {
        num = num.toString();
        // get integer and decimal part
        let [integer, decimal] = num.split('.');
        while (integer.length < size) {
            integer = '0' + integer;
        }
        return decimal ? `${integer}.${decimal}` : integer;
    }
    /**
     * checks whether the min symbol is allowed. It is allowed when minus symbol
     * provided and min input is undefined or min provided and is negative.
     * @param key The key string coming from an event
     */
    isMinusAllowed(key) {
        return (!this.min || this.min < 0) ? key !== '-' : true;
    }
    /**
     * check whether the key entered is inside the min and
     * max range. Returns true if it's out of range.
     * @param e A keyboard event
     */
    isOutSideRange(e) {
        if (!this.cleaveInstance) {
            return false;
        }
        // get caret position
        const pos = e.target['selectionStart'];
        // get caret position (in case there's text selected)
        const endPos = e.target['selectionEnd'];
        // get current value (formatted)
        let value = e.target['value'];
        // remove key from value
        if (e.key === 'Delete' || e.key === 'Backspace') {
            // check if there's selection of text to be removed
            if (pos !== endPos) {
                value = e.target['value'].substring(0, pos) + e.target['value'].substring(endPos, e.target['value'].length);
            }
            else {
                value = e.target['value'].substring(0, pos - 1) + e.target['value'].substring(endPos, e.target['value'].length);
            }
        }
        else {
            // add key to the value
            value = [value.slice(0, pos), e.key, value.slice(endPos)].join('');
        }
        // create a Cleave instance to extract the number from formatted value
        const cleave = new Cleave(document.createElement('input'), { ...this.options });
        cleave.setRawValue(value);
        const number = Number(cleave.getRawValue());
        // destroy cleave instance
        cleave.destroy();
        return (this.min > number || this.max < number);
    }
    /**
     * Decimal adjustment of a number.
     *
     * @param {Number}  value The number.
     * @param {Integer} exp   The exponent (the 10 logarithm of the adjustment base).
     * @returns {Number} The adjusted value.
     */
    decimalAdjust(value, exp) {
        // If the exp is undefined or zero...
        if (typeof exp === 'undefined' || +exp === 0) {
            return Math.round(value);
        }
        value = +value;
        exp = +exp;
        // If the value is not a number or the exp is not an integer...
        if (isNaN(value) || !(typeof exp === 'number' && exp % 1 === 0)) {
            return NaN;
        }
        // Shift
        let cleaned = value.toString().split('e');
        value = Math.round(+(cleaned[0] + 'e' + (cleaned[1] ? (+cleaned[1] - exp) : -exp)));
        // Shift back
        cleaned = value.toString().split('e');
        return +(cleaned[0] + 'e' + (cleaned[1] ? (+cleaned[1] + exp) : exp));
    }
}
/** @nocollapse */ EuiInputNumberComponent.ɵfac = i0.ɵɵngDeclareFactory({ minVersion: "12.0.0", version: "15.0.3", ngImport: i0, type: EuiInputNumberComponent, deps: [{ token: i1.NgControl, optional: true, self: true }, { token: i0.ElementRef }, { token: i0.Renderer2 }, { token: i0.ChangeDetectorRef }, { token: i2.LocaleService }, { token: i0.Injector }], target: i0.ɵɵFactoryTarget.Component });
/** @nocollapse */ EuiInputNumberComponent.ɵcmp = i0.ɵɵngDeclareComponent({ minVersion: "14.0.0", version: "15.0.3", type: EuiInputNumberComponent, selector: "input[euiInputNumber]:not([euiInputCheckBox])", inputs: { min: "min", max: "max", leadingZero: "leadingZero", isInvalid: "isInvalid", fractionDigits: "fractionDigits", digits: "digits", fillFraction: "fillFraction", roundUp: "roundUp", noFormat: "noFormat", styleClass: "styleClass", value: "value" }, host: { listeners: { "paste": "onPaste($event)", "keydown": "onKeyDown($event)", "focusout": "onFocusout($event)" }, properties: { "class": "this.cssClasses", "attr.type": "this.type" } }, usesInheritance: true, usesOnChanges: true, ngImport: i0, template: '', isInline: true, styles: [".eui-input-number{overflow:hidden;text-overflow:ellipsis;white-space:nowrap;font: 400 1rem/1.25rem arial,sans-serif;-webkit-appearance:none;appearance:none;background-color:var(--eui-base-color-white);border:1px solid var(--eui-base-color-grey-50);border-radius:var(--eui-base-border-radius);color:var(--eui-base-color-grey-100);display:block;padding:calc(var(--eui-base-spacing-s) - 1px);width:100%}.eui-input-number:active:not([readonly]),.eui-input-number:focus:not([readonly]){z-index:auto;border-color:var(--eui-base-color-accessible-focus)!important;box-shadow:inset 0 0 0 1px var(--eui-base-color-accessible-focus),inset 0 0 0 2px var(--eui-base-color-grey-5);transition:border-color .15s ease-in-out,box-shadow .15s ease-in-out}.eui-input-number[readonly]{background-color:transparent;border-color:transparent!important;box-shadow:none;height:inherit;padding:var(--eui-base-spacing-2xs) 0;pointer-events:none}.eui-input-number[readonly]+.eui-feedback-message,.eui-input-number[readonly]+.eui-input-maxlength{-webkit-appearance:none;appearance:none;display:none}.eui-input-number[disabled],.eui-input-number--disabled{background-color:var(--eui-base-color-grey-5);border:1px solid var(--eui-base-color-grey-25);color:var(--eui-base-color-grey-75);pointer-events:none}.eui-input-number--invalid,.eui-input-number--danger{border:1px solid var(--eui-base-color-danger-100)}.eui-input-number--clearable{padding-right:var(--eui-base-spacing-2xl)}.eui-input-number--clearable-icon{visibility:visible;position:absolute;right:var(--eui-base-spacing-s);cursor:pointer;-webkit-user-select:none;user-select:none}.eui-input-number--clearable-icon svg.eui-icon-svg{fill:var(--eui-base-color-grey-35)}.eui-input-number--clearable-icon svg.eui-icon-svg:hover{fill:var(--eui-base-color-grey-100)}.eui-input-number--loading{padding-right:var(--eui-base-spacing-2xl)}.eui-input-number--loading-icon{align-items:center;display:flex}.eui-input-number--loading-icon:after,.eui-input-number--loading-icon:before{content:\"\";height:var(--eui-base-spacing-l);position:absolute;right:var(--eui-base-spacing-s);width:var(--eui-base-spacing-l)}.eui-input-number--loading-icon:before{border:var(--eui-base-spacing-2xs) solid rgba(0,0,0,.35);border-radius:100%}.eui-input-number--loading-icon:after{animation:eui-loading .6s linear;animation-iteration-count:infinite;border-color:var(--eui-base-color-white) transparent transparent;border-radius:100%;border-style:solid;border-width:var(--eui-base-spacing-2xs);box-shadow:0 0 0 1px transparent}.eui-input-number--loading-icon--sm:after,.eui-input-number--loading-icon--sm:before{height:var(--eui-base-spacing-m);width:var(--eui-base-spacing-m)}@keyframes eui-loading{to{transform:rotate(360deg)}}.eui-input-number[readonly]{pointer-events:auto}\n"], encapsulation: i0.ViewEncapsulation.None });
i0.ɵɵngDeclareClassMetadata({ minVersion: "12.0.0", version: "15.0.3", ngImport: i0, type: EuiInputNumberComponent, decorators: [{
            type: Component,
            args: [{ selector: 'input[euiInputNumber]:not([euiInputCheckBox])', template: '', encapsulation: ViewEncapsulation.None, styles: [".eui-input-number{overflow:hidden;text-overflow:ellipsis;white-space:nowrap;font: 400 1rem/1.25rem arial,sans-serif;-webkit-appearance:none;appearance:none;background-color:var(--eui-base-color-white);border:1px solid var(--eui-base-color-grey-50);border-radius:var(--eui-base-border-radius);color:var(--eui-base-color-grey-100);display:block;padding:calc(var(--eui-base-spacing-s) - 1px);width:100%}.eui-input-number:active:not([readonly]),.eui-input-number:focus:not([readonly]){z-index:auto;border-color:var(--eui-base-color-accessible-focus)!important;box-shadow:inset 0 0 0 1px var(--eui-base-color-accessible-focus),inset 0 0 0 2px var(--eui-base-color-grey-5);transition:border-color .15s ease-in-out,box-shadow .15s ease-in-out}.eui-input-number[readonly]{background-color:transparent;border-color:transparent!important;box-shadow:none;height:inherit;padding:var(--eui-base-spacing-2xs) 0;pointer-events:none}.eui-input-number[readonly]+.eui-feedback-message,.eui-input-number[readonly]+.eui-input-maxlength{-webkit-appearance:none;appearance:none;display:none}.eui-input-number[disabled],.eui-input-number--disabled{background-color:var(--eui-base-color-grey-5);border:1px solid var(--eui-base-color-grey-25);color:var(--eui-base-color-grey-75);pointer-events:none}.eui-input-number--invalid,.eui-input-number--danger{border:1px solid var(--eui-base-color-danger-100)}.eui-input-number--clearable{padding-right:var(--eui-base-spacing-2xl)}.eui-input-number--clearable-icon{visibility:visible;position:absolute;right:var(--eui-base-spacing-s);cursor:pointer;-webkit-user-select:none;user-select:none}.eui-input-number--clearable-icon svg.eui-icon-svg{fill:var(--eui-base-color-grey-35)}.eui-input-number--clearable-icon svg.eui-icon-svg:hover{fill:var(--eui-base-color-grey-100)}.eui-input-number--loading{padding-right:var(--eui-base-spacing-2xl)}.eui-input-number--loading-icon{align-items:center;display:flex}.eui-input-number--loading-icon:after,.eui-input-number--loading-icon:before{content:\"\";height:var(--eui-base-spacing-l);position:absolute;right:var(--eui-base-spacing-s);width:var(--eui-base-spacing-l)}.eui-input-number--loading-icon:before{border:var(--eui-base-spacing-2xs) solid rgba(0,0,0,.35);border-radius:100%}.eui-input-number--loading-icon:after{animation:eui-loading .6s linear;animation-iteration-count:infinite;border-color:var(--eui-base-color-white) transparent transparent;border-radius:100%;border-style:solid;border-width:var(--eui-base-spacing-2xs);box-shadow:0 0 0 1px transparent}.eui-input-number--loading-icon--sm:after,.eui-input-number--loading-icon--sm:before{height:var(--eui-base-spacing-m);width:var(--eui-base-spacing-m)}@keyframes eui-loading{to{transform:rotate(360deg)}}.eui-input-number[readonly]{pointer-events:auto}\n"] }]
        }], ctorParameters: function () { return [{ type: i1.NgControl, decorators: [{
                    type: Optional
                }, {
                    type: Self
                }] }, { type: i0.ElementRef }, { type: i0.Renderer2 }, { type: i0.ChangeDetectorRef }, { type: i2.LocaleService }, { type: i0.Injector }]; }, propDecorators: { min: [{
                type: Input
            }], max: [{
                type: Input
            }], leadingZero: [{
                type: Input
            }], isInvalid: [{
                type: Input
            }], cssClasses: [{
                type: HostBinding,
                args: ['class']
            }], fractionDigits: [{
                type: Input
            }], digits: [{
                type: Input
            }], fillFraction: [{
                type: Input
            }], roundUp: [{
                type: Input
            }], noFormat: [{
                type: Input
            }], styleClass: [{
                type: Input
            }], type: [{
                type: HostBinding,
                args: ['attr.type']
            }], value: [{
                type: Input
            }], onPaste: [{
                type: HostListener,
                args: ['paste', ['$event']]
            }], onKeyDown: [{
                type: HostListener,
                args: ['keydown', ['$event']]
            }], onFocusout: [{
                type: HostListener,
                args: ['focusout', ['$event']]
            }] } });

class EuiInputNumberDirectiveModule {
}
/** @nocollapse */ EuiInputNumberDirectiveModule.ɵfac = i0.ɵɵngDeclareFactory({ minVersion: "12.0.0", version: "15.0.3", ngImport: i0, type: EuiInputNumberDirectiveModule, deps: [], target: i0.ɵɵFactoryTarget.NgModule });
/** @nocollapse */ EuiInputNumberDirectiveModule.ɵmod = i0.ɵɵngDeclareNgModule({ minVersion: "14.0.0", version: "15.0.3", ngImport: i0, type: EuiInputNumberDirectiveModule, declarations: [EuiInputNumberComponent], imports: [CommonModule], exports: [EuiInputNumberComponent] });
/** @nocollapse */ EuiInputNumberDirectiveModule.ɵinj = i0.ɵɵngDeclareInjector({ minVersion: "12.0.0", version: "15.0.3", ngImport: i0, type: EuiInputNumberDirectiveModule, imports: [CommonModule] });
i0.ɵɵngDeclareClassMetadata({ minVersion: "12.0.0", version: "15.0.3", ngImport: i0, type: EuiInputNumberDirectiveModule, decorators: [{
            type: NgModule,
            args: [{
                    imports: [
                        CommonModule,
                    ],
                    declarations: [
                        EuiInputNumberComponent,
                    ],
                    exports: [
                        EuiInputNumberComponent,
                    ],
                }]
        }] });

/**
 * Generated bundle index. Do not edit.
 */

export { EuiInputNumberComponent, EuiInputNumberDirectiveModule };
